import { ChangeDetectorRef, Component, EventEmitter, OnChanges, OnInit, Output, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  faTimes,
  faRobot,
  faDotCircle,
  faCircle,
  faArrowRight,
  faArrowLeft,
  faMagicWandSparkles,
  faSpinner,
  faCheckCircle,
  faFile
} from '@fortawesome/free-solid-svg-icons';
import { Archive } from '../../services/archives.service';

export interface ChatAssistantResult {
  wishList: any[];
  generalRules: string[];
  generalQuestions: any[];
}

@Component({
  selector: 'app-chat-assistant',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    TranslateModule,
    NgSelectModule
  ],
  templateUrl: './chat-assistant.component.html',
  styleUrls: ['./chat-assistant.component.scss']
})
export class ChatAssistantComponent implements OnInit, OnChanges {
  @Output() chatResult = new EventEmitter<{ archive_id: string; archive_ids: string[]; language: string; clear_delfos_cache: boolean; clear_di2winocr_cache: boolean; ocr_features_config_id?: string; delfos_llm_config_id?: string }>();
  @Output() closeChat = new EventEmitter<void>();

  @Input() archives: Archive[] = [];
  @Input() selectedArchiveId: string | number | null = null;
  @Input() loading = false;
  @Input() modelType: 'extraction' | 'classification' = 'extraction';
  @Input() ocrFeaturesConfigs: any[] = [];
  @Input() selectedOcrFeaturesId: string = '';
  @Input() delfosLlmConfigs: any[] = [];
  @Input() selectedDelfosLlmId: string = '';

  chatForm: FormGroup;
  step = 1;
  totalSteps = 1;
  clearDelfosCache = false;
  clearOcrCache = false;

  icons = {
    times: faTimes,
    robot: faRobot,
    dotCircle: faDotCircle,
    circle: faCircle,
    arrowRight: faArrowRight,
    arrowLeft: faArrowLeft,
    wand: faMagicWandSparkles,
    spinner: faSpinner,
    check: faCheckCircle,
    file: faFile
  };

  constructor(private fb: FormBuilder, private changeDetectorRef: ChangeDetectorRef) {
    this.chatForm = this.fb.group({
      archive_id: [[], Validators.required],
      ocr_features_config_id: [null]
    });
  }

  ngOnInit(): void {
    this.syncInitialSelections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.chatForm) return;
    if (changes['archives'] || changes['selectedArchiveId'] || changes['ocrFeaturesConfigs'] ||
        changes['selectedOcrFeaturesId'] || changes['delfosLlmConfigs'] || changes['selectedDelfosLlmId']) {
      this.syncInitialSelections();
      this.changeDetectorRef.markForCheck();
    }
  }

  getConfigurationLabel(config: any): string {
    return config?.display_label || config?.name || config?.key || '';
  }

  private syncInitialSelections(): void {
    if (this.archives.length > 0 && this.selectedArchiveId !== null &&
        this.selectedArchiveId !== undefined && this.selectedArchiveId !== '') {
      const selectedIds = Array.isArray(this.selectedArchiveId) ? this.selectedArchiveId : [this.selectedArchiveId];
      const resolvedIds = selectedIds
        .map(id => this.archives.find(archive => String(archive.id) === String(id))?.id)
        .filter((id): id is string | number => id !== undefined && id !== null);

      this.chatForm.patchValue({ archive_id: [] }, { emitEvent: false });
      this.chatForm.patchValue({ archive_id: resolvedIds }, { emitEvent: false });
    } else if (this.archives.length === 0) {
      this.chatForm.patchValue({ archive_id: [] }, { emitEvent: false });
    }

    if (this.selectedOcrFeaturesId) {
      const resolvedId = this.ocrFeaturesConfigs.find(
        config => String(config.id) === String(this.selectedOcrFeaturesId)
      )?.id ?? this.selectedOcrFeaturesId;
      this.chatForm.patchValue({ ocr_features_config_id: resolvedId }, { emitEvent: false });
    }

    if (this.selectedDelfosLlmId) {
      this.selectedDelfosLlmId = this.delfosLlmConfigs.find(
        config => String(config.id) === String(this.selectedDelfosLlmId)
      )?.id ?? this.selectedDelfosLlmId;
    }
  }

  get showOcrFeaturesSelector(): boolean {
    return this.ocrFeaturesConfigs.length > 1;
  }

  get showCacheOptions(): boolean {
    return this.archives.length > 1 || this.ocrFeaturesConfigs.length > 1;
  }

  get introKey(): string {
    return this.modelType === 'classification' ? 'ASSISTANT.INTRO_CLASSIFICATION' : 'ASSISTANT.INTRO_EXTRACTION';
  }

  get archiveControl(): FormControl {
    return this.chatForm.get('archive_id') as FormControl;
  }

  get progressPercent(): number {
    return (this.step / this.totalSteps) * 100;
  }

  generateResult(): void {
    if (this.chatForm.invalid) return;

    const { archive_id, ocr_features_config_id } = this.chatForm.value;
    const rawIds = Array.isArray(archive_id) ? archive_id : (archive_id ? [archive_id] : []);
    const archiveIds = rawIds.map(String);
    const language = sessionStorage.getItem('selectedLanguage') || 'pt';
    this.chatResult.emit({
      archive_id: archiveIds[0] || '',
      archive_ids: archiveIds,
      language,
      clear_delfos_cache: this.clearDelfosCache,
      clear_di2winocr_cache: this.clearOcrCache,
      ...(ocr_features_config_id ? { ocr_features_config_id } : {}),
      ...(this.selectedDelfosLlmId ? { delfos_llm_config_id: this.selectedDelfosLlmId } : {})
    });
  }

  close(): void {
    if (!this.loading) {
      this.closeChat.emit();
    }
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }
}
